// The one month list lives in when.ts ("Sept", not "Sep" — R16); re-exported here
// for the existing importers of utils.MONTHS.
import { MONTHS } from './when';
export { MONTHS };
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 1st, 2nd, 3rd, 4th … and the three teens that break the pattern. */
function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** "11:43 PM" — no leading zero on the hour, as a clock is read aloud. */
function clock(d: Date): string {
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
}

/** Midnight at the start of whichever day this falls on, in local time. */
const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * WHEN SOMETHING HAPPENED, FOR A HISTORY YOU ARE READING BACK.
 *
 * "3d ago" answers how long, which is the wrong question here. Reading a
 * history you are placing events against your own memory of a day — what you
 * were doing when it happened, what came before it — and for that you need a
 * clock and a day, not an elapsed count.
 *
 * So it stays relative only while relative is genuinely more useful, which is
 * the couple of hours where "20m ago" beats "9:15 PM", and becomes a real time
 * after that. Each step down adds exactly the context the previous one has
 * stopped carrying:
 *
 *   under 2 hours     just now · 20m ago · 1h ago
 *   later that day    11:43 PM
 *   the day before    Yesterday, 11:43 PM
 *   earlier this week Monday, 11:43 PM
 *   before that       Aug 1st, 11:43 PM
 *   another year      Aug 1st 2025, 11:43 PM
 *
 * A WEEK RUNS FROM SUNDAY. Naming a weekday only works while there is one of
 * them in recent memory — "Monday" nine days ago is ambiguous and reads as the
 * wrong Monday — so the moment a date falls before this week's Sunday it is
 * given as a date instead.
 *
 * `now` is a parameter so the awkward cases can be checked against a chosen
 * moment rather than whenever the tests happen to run.
 */
export function historyTime(ts: number, now: number = Date.now()): string {
  const mins = Math.floor((now - ts) / 60000);
  // A clock a little ahead of the server is not a reason to print a future time.
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 120) return '1h ago';

  const when = new Date(ts);
  const today = new Date(now);
  const at = clock(when);
  const day = dayStart(when);

  if (day === dayStart(today)) return at;
  if (day === new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1).getTime()) {
    return `Yesterday, ${at}`;
  }
  const sunday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay()).getTime();
  if (day >= sunday) return `${DAYS[when.getDay()]}, ${at}`;

  const year = when.getFullYear() === today.getFullYear() ? '' : ` ${when.getFullYear()}`;
  return `${MONTHS[when.getMonth()]} ${ordinal(when.getDate())}${year}, ${at}`;
}

export function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return asDate(ts);
}

/**
 * THE APP'S ONE WRITTEN DATE — "10 Sep 2026".
 *
 * en-GB is named explicitly, not left to the machine's locale, because a bare
 * `toLocaleDateString()` renders in whatever locale the browser runs in — which
 * put US `6/15/2026` and `8/23/2026` in front of people on an otherwise
 * day-first app (found across the Backlog, the card modal, Releases and "went
 * live"). Every surface that prints a full date routes through this, so a date
 * reads the same wherever it appears.
 */
export const asDate = (ts: number): string =>
  new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * A DATE INPUT WILL HAPPILY HAND YOU THE YEAR 26.
 *
 * Type "26" into the year segment of `<input type="date">` and it produces
 * 0026-10-08 — a real date, two thousand years ago, which the field then shows
 * back as 08/10/0026. The input is not broken; it was asked for the year 26 and
 * gave it. But nobody entering two digits into a project's dates means the third
 * century, and the wrong value is stored silently, so the first anybody knows is
 * a document dated in antiquity.
 *
 * So a year below 100 is read as this century's: 26 becomes 2026. Every other
 * year is left exactly as typed, including a genuinely old one — the correction
 * only applies where the alternative is certainly wrong.
 */
export function fullYear(date: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parts) return date;
  const [, year, month, day] = parts;
  return Number(year) >= 100 ? date : `${2000 + Number(year)}-${month}-${day}`;
}

/**
 * A DATE WITHOUT ITS YEAR — "17 Aug". For a place that shows a date whose year
 * its context already fixes — a sprint's own span, read as one line — where
 * repeating the year on each end is noise. Takes the ISO date string the date
 * fields store, and builds the date from its parts so the local timezone cannot
 * walk it back a day (`new Date('2026-08-17')` is parsed as UTC and would).
 * A full standalone date still routes through `asDate`.
 */
export const asDayMonth = (date: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fullYear(date));
  if (!m) return date;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

export function initials(name: string): string {
  if (!name.trim()) return '·';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}
